import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import localeText from "../../../../locale";
import UserEditService from "../../../../service/UserEditService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import {
  validateCPF,
  validateCNPJ,
  validateCodiceFiscale,
  validatePartitaIVA,
  validatePhoneNumber,
} from "../../../../utilities/documentValidation";
import "./UserInfoDialog.css";

const UserInfoDialog = ({ visible, onHide, onSuccess, userInfo }) => {
  const [firstname, setFirstname] = useState("");
  const [surname, setSurname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentType, setDocumentType] = useState("CPF"); // CPF, CNPJ, Codice Fiscale, Partita IVA
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { showSuccessToast, showErrorToast } = useToastContext();
  const userEditService = new UserEditService();

  const locale = localStorage.getItem("__extraconomy_locale");
  const isPT = locale === "pt" || locale === "pt-BR";
  const isIT = locale === "it" || locale === "it-IT";

  useEffect(() => {
    if (userInfo && visible) {
      setFirstname(userInfo.firstname || "");
      setSurname(userInfo.surname || "");
      setPhoneNumber(userInfo.phoneNumber || "");
      setDocumentNumber(userInfo.documentNumber || "");

      if (userInfo.documentNumber) {
        if (isPT) {
          const onlyNumbers = userInfo.documentNumber.replace(/\D/g, "");
          setDocumentType(onlyNumbers.length === 11 ? "CPF" : "CNPJ");
        } else if (isIT) {
          setDocumentType(userInfo.documentNumber.length === 16 ? "Codice Fiscale" : "Partita IVA");
        }
      } else {
        if (isPT) {
          setDocumentType("CPF");
        } else if (isIT) {
          setDocumentType("Codice Fiscale");
        }
      }
    }
  }, [userInfo, visible, isPT, isIT]);

  const validateForm = () => {
    const newErrors = {};

    if (!firstname || !firstname.trim()) {
      newErrors.firstname = localeText("pleaseFillOutAll");
    }

    if (!surname || !surname.trim()) {
      newErrors.surname = localeText("pleaseFillOutAll");
    }

    if (!phoneNumber || !phoneNumber.trim()) {
      newErrors.phoneNumber = localeText("pleaseFillOutAll");
    } else if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phoneNumber = localeText("invalidPhoneNumber");
    }

    if (!documentNumber || !documentNumber.trim()) {
      newErrors.documentNumber = localeText("pleaseFillOutAll");
    } else {
      let isValid = false;

      if (isPT) {
        if (documentType === "CPF") {
          isValid = validateCPF(documentNumber);
          if (!isValid) {
            newErrors.documentNumber = localeText("invalidCPF");
          }
        } else if (documentType === "CNPJ") {
          isValid = validateCNPJ(documentNumber);
          if (!isValid) {
            newErrors.documentNumber = localeText("invalidCNPJ");
          }
        }
      } else if (isIT) {
        if (documentType === "Codice Fiscale") {
          isValid = validateCodiceFiscale(documentNumber);
          if (!isValid) {
            newErrors.documentNumber = localeText("invalidCodiceFiscale");
          }
        } else if (documentType === "Partita IVA") {
          isValid = validatePartitaIVA(documentNumber);
          if (!isValid) {
            newErrors.documentNumber = localeText("invalidPartitaIVA");
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const username = userInfo?.username || localStorage.getItem("__extraconomy_username");

      if (!username) {
        showErrorToast({ detail: localeText("error") });
        setLoading(false);
        return;
      }

      const payload = {
        username: username,
        firstname: firstname.trim(),
        surname: surname.trim(),
        phoneNumber: phoneNumber.trim(),
        documentNumber: isPT
          ? documentNumber.replace(/\D/g, "")
          : documentNumber.trim().toUpperCase(),
        postalCode: userInfo?.postalCode || null,
        country: userInfo?.country || null,
        state: userInfo?.state || null,
        city: userInfo?.city || null,
        address: userInfo?.address || null,
        district: userInfo?.district || null,
        number: userInfo?.number || null,
        internationalCode: userInfo?.internationalCode || null,
        localCode: userInfo?.localCode || null,
      };

      const res = await userEditService.postChangeProfileInfo(
        localStorage.getItem("__extraconomy_token"),
        payload
      );

      if (res.messageCode !== 200000) {
        const isDocumentDuplicate =
          res.message &&
          (res.message.includes("constraint") ||
           res.message.includes("ConstraintViolationException") ||
           res.message.includes("uk_awvtb3krls8im69odhkitk708"));

        if (isDocumentDuplicate) {
          showErrorToast({
            detail: "documentAlreadyRegistered",
          });
        } else {
          showErrorToast({ code: res.messageCode });
        }
        setLoading(false);
        return;
      }

      showSuccessToast({ detail: "sucessfullyEditedProfile" });
      setLoading(false);
      onHide();
      if (onSuccess) {
        onSuccess(res.result);
      }
    } catch (error) {
      console.error("Error updating user info:", error);
      showErrorToast({ detail: localeText("error") });
      setLoading(false);
    }
  };

  const getDocumentTypeOptions = () => {
    if (isPT) {
      return [
        { label: "CPF", value: "CPF" },
        { label: "CNPJ", value: "CNPJ" },
      ];
    } else if (isIT) {
      return [
        { label: "Codice Fiscale", value: "Codice Fiscale" },
        { label: "Partita IVA", value: "Partita IVA" },
      ];
    }
    return [];
  };

  const getDocumentPlaceholder = () => {
    if (isPT) {
      return documentType === "CPF" ? "000.000.000-00" : "00.000.000/0000-00";
    } else if (isIT) {
      return documentType === "Codice Fiscale" ? "RSSMRA80A01H501U" : "00000000000";
    }
    return "";
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={localeText("cancel")}
        className="p-button-text"
        onClick={onHide}
        disabled={loading}
      />
      <Button
        label={localeText("save")}
        className="px-4 border-none"
        style={{
          backgroundColor: "#28AB69",
          color: "#FAFBFC",
          paddingTop: "12px",
          paddingBottom: "12px",
        }}
        onClick={handleSubmit}
        loading={loading}
      />
    </div>
  );

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">{localeText("userInfoDialogTitle")}</span>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      footer={footer}
      header={headerElement}
      style={{ width: "600px" }}
      className="user-info-dialog"
    >
      <div className="grid grid-nogutter gap-3">
        <div className="col-12">
          <p className="text-sm mb-3" style={{ color: "#505F79" }}>
            {localeText("userInfoDialogDescription")}
          </p>
        </div>

        <div className="col-12">
          <label htmlFor="firstname" className="block text-sm font-medium mb-2">
            {localeText("firstName")} *
          </label>
          <InputText
            id="firstname"
            value={firstname}
            onChange={(e) => {
              setFirstname(e.target.value);
              if (errors.firstname) {
                setErrors({ ...errors, firstname: null });
              }
            }}
            className={`w-full ${errors.firstname ? "p-invalid" : ""}`}
            placeholder={localeText("enterYourFirstName")}
          />
          {errors.firstname && <small className="p-error">{errors.firstname}</small>}
        </div>

        <div className="col-12">
          <label htmlFor="surname" className="block text-sm font-medium mb-2">
            {localeText("surname")} *
          </label>
          <InputText
            id="surname"
            value={surname}
            onChange={(e) => {
              setSurname(e.target.value);
              if (errors.surname) {
                setErrors({ ...errors, surname: null });
              }
            }}
            className={`w-full ${errors.surname ? "p-invalid" : ""}`}
            placeholder={localeText("enterYourLastName")}
          />
          {errors.surname && <small className="p-error">{errors.surname}</small>}
        </div>

        <div className="col-12">
          <label htmlFor="phoneNumber" className="block text-sm font-medium mb-2">
            {localeText("phone")} *
          </label>
          <InputText
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (errors.phoneNumber) {
                setErrors({ ...errors, phoneNumber: null });
              }
            }}
            className={`w-full ${errors.phoneNumber ? "p-invalid" : ""}`}
            placeholder="+55 11 98765-4321"
          />
          {errors.phoneNumber && <small className="p-error">{errors.phoneNumber}</small>}
        </div>

        <div className="col-12">
          <label className="block text-sm font-medium mb-2">{localeText("documentType")} *</label>
          <div className="flex gap-4">
            {getDocumentTypeOptions().map((option) => (
              <div key={option.value} className="flex align-items-center">
                <RadioButton
                  inputId={option.value}
                  name="documentType"
                  value={option.value}
                  onChange={(e) => {
                    setDocumentType(e.value);
                    setDocumentNumber("");
                    if (errors.documentNumber) {
                      setErrors({ ...errors, documentNumber: null });
                    }
                  }}
                  checked={documentType === option.value}
                />
                <label htmlFor={option.value} className="ml-2">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12">
          <label htmlFor="documentNumber" className="block text-sm font-medium mb-2">
            {documentType} *
          </label>
          <InputText
            id="documentNumber"
            value={documentNumber}
            onChange={(e) => {
              setDocumentNumber(e.target.value);
              if (errors.documentNumber) {
                setErrors({ ...errors, documentNumber: null });
              }
            }}
            className={`w-full ${errors.documentNumber ? "p-invalid" : ""}`}
            placeholder={getDocumentPlaceholder()}
          />
          {errors.documentNumber && <small className="p-error">{errors.documentNumber}</small>}
        </div>
      </div>
    </Dialog>
  );
};

export default UserInfoDialog;

